import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, of, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthTokenService } from '../auth/auth-token.service';
import { AppConfig, EMPTY_APP_CONFIG } from './app-config.model';

/**
 * Bootstrap data for the SPA.
 *
 * Fetched once at app init by an APP_INITIALIZER (see app.module.ts).
 * If the call fails (pipeline down, network error) we fall back to the
 * empty default and log — the SPA will still boot to its login page.
 */
@Injectable({ providedIn: 'root' })
export class ConfigService {
  private current: AppConfig = EMPTY_APP_CONFIG;

  constructor(private http: HttpClient, private tokens: AuthTokenService) {}

  get config(): AppConfig {
    return this.current;
  }

  hasRole(role: string): boolean {
    return this.current.roles.includes(role);
  }

  /** True if the named feature flag is on. Reads from /v1/config payload. */
  hasFeature(flag: string): boolean {
    return Boolean(this.current.featureFlags?.[flag]);
  }

  /** Called by APP_INITIALIZER. Always resolves — never rejects. */
  load(): Promise<void> {
    if (!this.tokens.isAuthenticated) {
      this.current = EMPTY_APP_CONFIG;
      return Promise.resolve();
    }
    const url = `${environment.apis.default.url}/v1/config`;
    return firstValueFrom(
      this.http.get<AppConfig>(url).pipe(
        tap(cfg => (this.current = cfg)),
        catchError(err => {
          // eslint-disable-next-line no-console
          console.warn('[ConfigService] /v1/config failed; using defaults.', err);
          this.current = EMPTY_APP_CONFIG;
          return of(EMPTY_APP_CONFIG);
        }),
      ),
    ).then(() => undefined);
  }
}

/** Factory used by APP_INITIALIZER. */
export function configInitializerFactory(svc: ConfigService): () => Promise<void> {
  return () => svc.load();
}
